import { ListResponse } from "@/api/base";
import {
  BusinessReportOutcome,
  BusinessReportOutcomeDetails,
  BusinessReportOutcomePermissionAccess,
  BusinessReportType,
  FamilySourceOutcome,
  PromptDataType,
} from "@/modules/snapshots/businessReports.types";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import { SnapshotSearchEvent } from "@/modules/snapshots/grid/sidebar/search/types";
import { templatesApi } from "./templates";

interface BusinessReportOutcomeListResponse extends ListResponse<BusinessReportOutcome> {
  has_failed_results: boolean;
}

/**
 * Report outcomes: runs, results, notes, permissions and access.
 * Cell-level edits (result/rating, note create, verified/name) live in `./grid` so they can patch the
 * cached grid pages instead of invalidating them.
 */
export const outcomesApi = templatesApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    deleteBusinessReportOutcomePermission: builder.mutation<void, { id: string; permissionId: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id, permissionId }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/permissions/${permissionId}`,
        method: "DELETE",
      }),
    }),
    retryBusinessReportOutcomeResult: builder.mutation<BusinessReportOutcomeDetails, { id: string; resultId: string }>({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG,
      ],
      query: ({ id, resultId }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/retry/${resultId}`,
        method: "POST",
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const businessReportOutcome = await queryFulfilled;
        dispatch(
          outcomesApi.util.updateQueryData("getBusinessReportOutcome", id, draft => {
            Object.assign(draft, businessReportOutcome);
          }),
        );
      },
    }),
    updateBusinessReportOutcomeAccess: builder.mutation<
      void,
      { id: string; access: BusinessReportOutcomePermissionAccess }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ id, access }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/access`,
        method: "PATCH",
        data: { access },
      }),
    }),
    createBusinessReportOutcomePermission: builder.mutation<void, { id: string; role: string; users: string[] }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id, role, users }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/permissions`,
        method: "POST",
        data: {
          permissions: users.map(user => ({ role, user_id: user, business_report_outcome_id: id })),
        },
      }),
    }),
    createBusinessReportOutcome: builder.mutation<
      {
        snapshot_collection_id: string;
        business_report_outcome_ids: string[];
      },
      {
        fs_entries: string[];
        business_report_id: string;
        name: string;
        variables: Record<string, string>;
        snapshot_collection_id?: string;
        snapshot_collection_new?: string;
        subcollection_id?: string;
        execution_mode?: "sync" | "batch";
        business_report_prompts: {
          order: number;
          business_report_prompt_id: string;
          prompt: string;
          name: string;
          full_context: boolean;
          is_date: boolean;
          data_type: PromptDataType | null;
          use_image: boolean;
          no_retriever: boolean;
          web_search: boolean;
          traffic_light: boolean;
        }[];
      }
    >({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG,
      ],
      query: ({
        name,
        fs_entries,
        business_report_id,
        variables,
        business_report_prompts,
        snapshot_collection_id,
        snapshot_collection_new,
        subcollection_id,
        execution_mode,
      }) => ({
        url: "/pf-mainweb/business-reports/business-report-outcome",
        method: "POST",
        data: {
          snapshot_collection_id,
          snapshot_collection_new,
          subcollection_id,
          fs_entries,
          name,
          business_report_id,
          business_report_prompts,
          variables,
          execution_mode,
        },
      }),
    }),
    createBusinessReportFromSnapshotCollection: builder.mutation<
      { business_report_outcome_id: string },
      {
        snapshot_collection_id: string;
        business_report_id: string;
        name: string;
        variables: Record<string, string>;
        business_report_prompts: {
          order: number;
          business_report_prompt_id: string;
          prompt: string;
          name: string;
          full_context: boolean;
          is_date: boolean;
          data_type: PromptDataType | null;
          use_image: boolean;
          no_retriever: boolean;
          web_search: boolean;
          traffic_light: boolean;
        }[];
      }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshot_collection_id, ...data }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshot_collection_id}/business-reports`,
        method: "POST",
        data,
      }),
    }),
    getBusinessReportsOutcome: builder.query<
      BusinessReportOutcomeListResponse,
      Partial<{
        snapshot_collection_id: string;
        subcollection_id?: string;
        offset: number;
        limit?: number;
        query?: string;
        descending?: boolean;
        order_by?: string;
        template_type?: BusinessReportType;
      }> | void
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: (params?) => ({
        url: "/pf-mainweb/business-reports/business-report-outcome",
        params,
      }),
    }),
    searchBusinessReportsOutcome: builder.query<
      SnapshotSearchEvent[],
      {
        business_report_id: string;
        search_id: string;
        snapshot_collection_id: string;
        subcollection_id?: string;
        query: string;
      }
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_SEARCH_TAG],
      query: params => ({
        url: "/pf-mainweb/business-reports/search",
        method: "POST",
        params,
      }),
    }),
    cancelBusinessReportOutcome: builder.mutation<void, { id: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/cancel`,
        method: "POST",
      }),
    }),
    deleteBusinessReportOutcome: builder.mutation<void, { id: string }>({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG,
      ],
      query: ({ id }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}`,
        method: "DELETE",
      }),
    }),
    deleteBusinessReportOutcomeNote: builder.mutation<void, { outcomeId: string; resultId: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ outcomeId, resultId }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${outcomeId}/notes/${resultId}`,
        method: "DELETE",
      }),
    }),
    getBusinessReportOutcome: builder.query<BusinessReportOutcomeDetails, string>({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}`,
      }),
    }),
    // Untagged: sources are read-only here, so outcome mutations on the page must not refetch them.
    getFamilySourceOutcomes: builder.query<FamilySourceOutcome[], string>({
      query: familyOutcomeId => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${familyOutcomeId}/family-sources`,
      }),
    }),
    suggestBusinessReportOutcomeResultName: builder.query<string, string>({
      query: resultId => ({
        url: `/pf-mainweb/business-reports/business-report-outcome-result/${resultId}/suggest-name`,
      }),
    }),
  }),
});

export const {
  useSearchBusinessReportsOutcomeQuery,
  useGetBusinessReportsOutcomeQuery,
  useGetBusinessReportOutcomeQuery,
  useGetFamilySourceOutcomesQuery,
  useCreateBusinessReportOutcomeMutation,
  useCreateBusinessReportFromSnapshotCollectionMutation,
  useDeleteBusinessReportOutcomePermissionMutation,
  useCreateBusinessReportOutcomePermissionMutation,
  useCancelBusinessReportOutcomeMutation,
  useDeleteBusinessReportOutcomeMutation,
  useDeleteBusinessReportOutcomeNoteMutation,
  useRetryBusinessReportOutcomeResultMutation,
  useUpdateBusinessReportOutcomeAccessMutation,
  useLazySuggestBusinessReportOutcomeResultNameQuery,
} = outcomesApi;
